import { useQuery } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { OrganizationPerformanceReportingConfig } from '../../../_types/performanceReporting';

export async function getOrganizationPerformanceReportingConfig(
  orgId: string,
  signal?: AbortSignal
): Promise<OrganizationPerformanceReportingConfig> {
  if (!orgId) throw new Error('No organization ID provided.');

  const url = `${baseApiUrl()}/admin-solution/v1/organizations/${orgId}/solutions/performance-reporting/config`;

  // Real implementation (commented out until backend is ready):
  try {
    const response = await fetcher({
      method: 'GET',
      url,
      options: { signal },
    });
    return response;
  } catch (error: unknown) {
    if (typeof error === 'object' && error !== null && 'errorDetails' in error) {
      throw error as ServerErrorResponse;
    }
    throw error;
  }
}

export default function useOrganizationPerformanceReportingConfig(
  orgId: string,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  return useQuery<OrganizationPerformanceReportingConfig, ServerErrorResponse>(
    ['organizationPerformanceReportingConfig', orgId],
    async ({ signal }): Promise<OrganizationPerformanceReportingConfig> => {
      try {
        return await getOrganizationPerformanceReportingConfig(orgId, signal);
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      enabled: !!orgId,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      ...options,
      keepPreviousData: true,
    }
  );
}
